import { useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import {
  useCategories,
  useCreatePricingRule,
  useDeletePricingRule,
  useMentors,
  usePricingRules,
  useSessionTypes,
  useSettings,
  useUpdatePricingRule,
} from '@/lib/queries'
import { PageHeader, Card, Callout, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Button } from '@/components/ui/Button'
import { Modal, ConfirmDialog } from '@/components/ui/Modal'
import { SelectField, TextField } from '@/components/ui/Field'
import { Pill, type PillTone } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { formatMoney } from '@/lib/format'
import type { PricingRule, PricingStrategy } from '@/types/api'
import styles from './Masters.module.css'

/**
 * How specific each strategy is. Higher wins when several rules match.
 *
 * *** THIS ORDER IS THE WHOLE MENTAL MODEL ***
 * An admin sets a flat ₹500, then ₹800 for one mentor, and needs to know which
 * one a student actually pays. Showing the rules in a flat list sorted by
 * creation date makes that impossible to reason about, so the table is ordered
 * by specificity and labelled with it.
 */
const STRATEGY_SPECIFICITY: Record<PricingStrategy, number> = {
  PER_MENTOR_SESSION_TYPE: 5,
  PER_MENTOR: 4,
  PER_SESSION_TYPE: 3,
  PER_CATEGORY: 2,
  FLAT: 1,
}

/**
 * Where each rule sits in the contest, in the server's own order:
 *
 *   PER_MENTOR_SESSION_TYPE > PER_MENTOR > PER_CATEGORY > PER_SESSION_TYPE > FLAT
 *
 * *** FIVE LEVELS, NOT TWO ***
 * This column used to render only "Fallback" or "More specific", so a
 * mentor+session-type rule and a session-type rule looked identical even
 * though the first always beats the second. An admin with several rules could
 * not tell which one would actually be charged.
 *
 * Worded as an ordinal rather than a specificity score: "1st choice" answers
 * the admin's real question — which rule wins — without needing the concept of
 * specificity at all.
 */
const RULE_ORDER: Record<
  PricingStrategy,
  { label: string; tone: PillTone }
> = {
  PER_MENTOR_SESSION_TYPE: { label: '1st choice', tone: 'indigo' },
  PER_MENTOR: { label: '2nd choice', tone: 'info' },
  PER_CATEGORY: { label: '3rd choice', tone: 'teal' },
  PER_SESSION_TYPE: { label: '4th choice', tone: 'warning' },
  FLAT: { label: 'Last resort', tone: 'neutral' },
}

const STRATEGY_LABELS: Record<PricingStrategy, string> = {
  FLAT: 'Everything',
  PER_CATEGORY: 'One category',
  PER_SESSION_TYPE: 'One session type',
  PER_MENTOR: 'One mentor',
  PER_MENTOR_SESSION_TYPE: 'One mentor + session type',
}

/** Which target fields each strategy needs. */
const STRATEGY_TARGETS: Record<
  PricingStrategy,
  Array<'mentor' | 'category' | 'session_type'>
> = {
  FLAT: [],
  PER_CATEGORY: ['category'],
  PER_SESSION_TYPE: ['session_type'],
  PER_MENTOR: ['mentor'],
  PER_MENTOR_SESSION_TYPE: ['mentor', 'session_type'],
}

/**
 * A5 — Pricing rules.
 *
 * Resolution is MOST SPECIFIC WINS. The screen's job is making that visible:
 * rules are grouped by how specific they are, and the fallback rule is called
 * out because its absence means some bookings have no price at all.
 */
export function PricingRulesPage() {
  const toast = useToast()
  const { data, isLoading, error, refetch } = usePricingRules()
  const categories = useCategories()
  const sessionTypes = useSessionTypes()
  const mentors = useMentors()

  const settings = useSettings()
  const { user } = useAuth()

  const createMutation = useCreatePricingRule()
  const updateMutation = useUpdatePricingRule()
  const deleteMutation = useDeletePricingRule()

  /**
   * Per-session pricing does not apply to a subscription client.
   *
   * *** READ-ONLY, NOT HIDDEN ***
   * The rules stay visible because they are real configuration that becomes
   * live again if the billing model is switched back — and an admin looking
   * for pricing needs to find out WHY it is off, not conclude the portal is
   * broken. Every control that would change something is disabled.
   *
   * Only once settings have loaded: assuming subscription while the request is
   * in flight would grey out the page on every visit for a per-session client.
   */
  // *** READ FROM THE SESSION, NOT FROM /admin/settings ***
  // That endpoint is Admin-only. An Accountant got a 403, `settings.data` came
  // back undefined, and this line silently decided "per session" — so on a
  // SUBSCRIPTION client they saw a fully editable pricing screen with no
  // warning, while the Admin saw it correctly greyed out. The mode is a fact
  // about the CLIENT, so it now travels with the session and every role reads
  // the same answer.
  //
  // `settings.data` is still preferred when present: an Admin who switches
  // billing sees it take effect without signing in again.
  const isSubscription =
    (settings.data?.billing_mode ?? user?.billing_mode) === 'SUBSCRIPTION'

  const [editing, setEditing] = useState<PricingRule | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<PricingRule | null>(null)
  const [draftStrategy, setDraftStrategy] = useState<PricingStrategy>('FLAT')

  const rules = useMemo(() => {
    const list = data?.rules ?? []
    return [...list].sort(
      (a, b) =>
        STRATEGY_SPECIFICITY[b.strategy] - STRATEGY_SPECIFICITY[a.strategy] ||
        b.priority - a.priority,
    )
  }, [data])

  const hasFallback = rules.some(
    (rule) => rule.strategy === 'FLAT' && rule.is_active,
  )

  // *** A DISABLED BUTTON IS PRESENTATION, NOT ENFORCEMENT ***
  // Guarding here too means a keyboard activation, a stale render, or a
  // future refactor that forgets a `disabled` prop cannot open an editor for
  // rules this client does not use.
  const openCreate = () => {
    if (isSubscription) return
    setDraftStrategy('FLAT')
    setCreating(true)
  }

  const openEdit = (rule: PricingRule) => {
    if (isSubscription) return
    setDraftStrategy(rule.strategy)
    setEditing(rule)
  }

  const closeForm = () => {
    setCreating(false)
    setEditing(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const targets = STRATEGY_TARGETS[draftStrategy]

    const input = {
      strategy: draftStrategy,
      // Only send the ids this strategy uses. Sending a stale mentor_id on a
      // FLAT rule would make it look mentor-specific to the resolver.
      mentor_id: targets.includes('mentor')
        ? String(form.get('mentor_id') ?? '') || null
        : null,
      category_id: targets.includes('category')
        ? String(form.get('category_id') ?? '') || null
        : null,
      session_type_id: targets.includes('session_type')
        ? String(form.get('session_type_id') ?? '') || null
        : null,
      base_amount: String(form.get('base_amount') ?? '0').trim(),
      priority: Number(form.get('priority') ?? 0),
    }

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.rule_id, input })
        toast.success('Pricing rule updated.')
      } else {
        await createMutation.mutateAsync(input)
        toast.success('Pricing rule added.')
      }
      closeForm()
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save the rule.',
      )
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteMutation.mutateAsync(deleting.rule_id)
      toast.success('Pricing rule removed.')
      setDeleting(null)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not remove it.',
      )
      setDeleting(null)
    }
  }

  const describeTarget = (rule: PricingRule): string => {
    const parts: string[] = []
    if (rule.mentor_name) parts.push(rule.mentor_name)
    if (rule.category_name) parts.push(rule.category_name)
    if (rule.session_type_name) parts.push(rule.session_type_name)
    return parts.length > 0 ? parts.join(' · ') : 'All sessions'
  }

  const columns: Array<Column<PricingRule>> = [
    {
      key: 'applies',
      header: 'Applies to',
      render: (row) => (
        <div>
          <span className={styles.primaryText}>{describeTarget(row)}</span>
          <p className={styles.secondaryText}>
            {STRATEGY_LABELS[row.strategy]}
          </p>
        </div>
      ),
    },
    {
      key: 'specificity',
      // "Wins over" described a relationship without naming the other side —
      // wins over what? "Rule order" says what the column is for in words an
      // admin already uses.
      header: 'Rule order',
      render: (row) => (
        <Pill tone={RULE_ORDER[row.strategy]?.tone ?? 'neutral'}>
          {RULE_ORDER[row.strategy]?.label ?? row.strategy}
        </Pill>
      ),
    },
    {
      key: 'amount',
      header: 'Price',
      align: 'right',
      render: (row) => (
        <span className={`${styles.primaryText} numeric`}>
          {formatMoney(row.base_amount, row.currency)}
        </span>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      align: 'right',
      secondary: true,
      render: (row) => <span className="numeric">{row.priority}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.is_active ? (
          <Pill tone="success">Active</Pill>
        ) : (
          <Pill tone="neutral">Inactive</Pill>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '150px',
      // Disabled rather than removed for a subscription client: an empty
      // actions column looks like a rendering fault, a greyed one reads as a
      // deliberate state — and the callout above says why.
      render: (row) => (
        <div className={styles.rowActions}>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openEdit(row)}
            disabled={isSubscription}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDeleting(row)}
            disabled={isSubscription}
          >
            Remove
          </Button>
        </div>
      ),
    },
  ]

  const targets = STRATEGY_TARGETS[draftStrategy]

  return (
    <PageStack>
      <PageHeader
        title="Pricing rules"
        description="A booking can match several rules. The one highest in the rule order is the price charged."
        actions={
          <Button variant="primary" onClick={openCreate} disabled={isSubscription}>
            Add rule
          </Button>
        }
      />

      {isSubscription && (
        <Callout tone="info" title="Not used by this organisation">
          <p>
            This organisation bills by <strong>subscription</strong>, so
            students pay for a plan rather than for each booking. Pricing rules
            have no effect and cannot be edited.
          </p>
          <p>
            Any rules below are kept as they are, and become live again only if
            the platform team switches this organisation back to per-session
            billing.
          </p>
        </Callout>
      )}

      {/*
        Deliberately suppressed for a subscription client: "No fallback price"
        warns that bookings cannot be completed, which is alarming and simply
        untrue when nothing is priced per session in the first place.
      */}
      {!isLoading && !hasFallback && !isSubscription && (
        <Callout tone="danger" title="No fallback price">
          <p>
            Nothing covers sessions that no other rule matches, so those
            bookings have no price and cannot be completed. Add a rule that
            applies to <strong>Everything</strong>.
          </p>
        </Callout>
      )}

      <Card flush>
        <Table
          columns={columns}
          rows={rules}
          rowKey={(row) => row.rule_id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          emptyMessage="No pricing rules yet. Start with one that applies to everything."
        />
      </Card>

      <Card title="How a price is chosen">
        <ol className={styles.rulesList}>
          <li>A mentor with a specific session type</li>
          <li>A mentor, any session type</li>
          <li>A session type, any mentor</li>
          <li>A category</li>
          <li>The fallback that applies to everything</li>
        </ol>
        <p className={styles.previewNote}>
          The first match going down this list is the price the student pays.
          Fees from the Fee heads screen are then added on top.
        </p>
      </Card>

      <Modal
        open={creating || editing !== null}
        onClose={closeForm}
        title={editing ? 'Edit pricing rule' : 'Add pricing rule'}
      >
        <form
          onSubmit={handleSubmit}
          className={styles.form}
          key={editing?.rule_id ?? 'new'}
        >
          <SelectField
            label="This price applies to"
            name="strategy"
            value={draftStrategy}
            onChange={(event) =>
              setDraftStrategy(event.target.value as PricingStrategy)
            }
            options={(
              Object.keys(STRATEGY_LABELS) as PricingStrategy[]
            ).map((strategy) => ({
              value: strategy,
              label: STRATEGY_LABELS[strategy],
            }))}
            hint="A more specific rule always beats a broader one."
          />

          {targets.includes('mentor') && (
            <SelectField
              label="Mentor"
              name="mentor_id"
              defaultValue={editing?.mentor_id ?? ''}
              placeholder="Choose a mentor"
              required
              options={(mentors.data?.mentors ?? []).map((mentor) => ({
                value: mentor.user_id,
                label: mentor.full_name,
              }))}
            />
          )}

          {targets.includes('category') && (
            <SelectField
              label="Category"
              name="category_id"
              defaultValue={editing?.category_id ?? ''}
              placeholder="Choose a category"
              required
              options={(categories.data?.categories ?? []).map((category) => ({
                value: category.category_id,
                label: category.name,
              }))}
            />
          )}

          {targets.includes('session_type') && (
            <SelectField
              label="Session type"
              name="session_type_id"
              defaultValue={editing?.session_type_id ?? ''}
              placeholder="Choose a session type"
              required
              options={(sessionTypes.data?.session_types ?? []).map((type) => ({
                value: type.session_type_id,
                label: `${type.name} (${type.duration_minutes} min)`,
              }))}
            />
          )}

          <TextField
            label="Price"
            name="base_amount"
            type="number"
            min={0}
            step="0.01"
            prefix="₹"
            defaultValue={editing?.base_amount ?? ''}
            required
            hint="Before fees. The student sees the full breakdown at checkout."
          />

          <TextField
            label="Priority"
            name="priority"
            type="number"
            min={0}
            defaultValue={String(editing?.priority ?? 0)}
            hint="Breaks ties between two rules of the same specificity. Higher wins."
          />

          <div className={styles.formActions}>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? 'Save changes' : 'Add rule'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Remove this pricing rule?"
        message={
          deleting?.strategy === 'FLAT' ? (
            <>
              This is the <strong>fallback</strong> rule. Removing it leaves
              sessions that match no other rule without a price, and students
              will not be able to book them.
            </>
          ) : (
            <>
              Sessions matching this rule will fall back to a broader one. Prices
              for those bookings will change.
            </>
          )
        }
        confirmLabel="Remove"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </PageStack>
  )
}
